// Cierra la sesión sola tras un rato sin actividad, pensado SOLO para
// computadores. Cuál aparato cuenta como computador lo decide `esEscritorio()`,
// con sus trampas abiertas anotadas en `helpers/idle-session.js` (#1211).
//
// El caso real: el médico se levanta del puesto del hospital y deja la ficha
// abierta. En el teléfono no aplica: es un aparato personal, con biometría, y
// sacarlo cada media hora sería insoportable.
//
// La regla es "en un computador no queda sesión abierta", y por eso hay dos
// piezas más: `helpers/idle-session.js` persiste la marca de actividad para que
// cerrar la pestaña no reinicie el reloj, y el flag `idle_logout_exempt` del
// usuario exime a quien trabaja siempre en los mismos equipos.
//
// Por qué vive en el cliente y no en el servidor: SmartDx guarda UN token por
// usuario, compartido por todos los aparatos. El servidor no puede distinguir
// "esta sesión es del PC" de "esta es del celular", así que una expiración
// server-side mataría las dos. El navegador del computador, en cambio, puede
// olvidarse el token localmente sin que el teléfono se entere.
//
// Lo que esto compra, y lo que NO: protege del que se sienta en el computador
// abierto, que es el riesgo real del hospital. NO protege de un token robado,
// porque en el servidor sigue siendo válido. Eso lo arregla el workstream de
// tokens por dispositivo.
import { useCallback, useEffect, useRef, useState } from "react";

import {
  AVISO_MS,
  IDLE_MS,
  esEscritorio,
  marcarActividad as persistirActividad,
  ultimaActividadConocida,
} from "../helpers/idle-session";

// Re-exportados: el umbral y la detección de aparato viven en el helper para que
// el chequeo de arranque no dependa de React, pero este sigue siendo el lugar
// natural donde buscarlos.
export { IDLE_MS, AVISO_MS, esEscritorio };

const EVENTOS = ["mousemove", "mousedown", "keydown", "wheel", "touchstart", "scroll"];

/**
 * @param {object} opts
 * @param {boolean} opts.enabled  — normalmente `!!currentUser`
 * @param {(motivo: "timer"|"al_volver") => void} opts.onLogout — el motivo va a
 *   la telemetría: sin él, "se cerró sola" y "se cerró al volver a la pestaña"
 *   se ven iguales, y son dos caminos distintos que pueden romperse por separado.
 * @param {() => boolean} opts.isBusy — true mientras haya trabajo en curso.
 *   NO es opcional en la práctica: en Turno hay dictados y generaciones del LLM
 *   que corren minutos sin que nadie toque el teclado. Si eso contara como
 *   inactividad, al médico lo sacan en la mitad de una epicrisis.
 */
export default function useIdleLogout({ enabled, onLogout, isBusy }) {
  const [avisando, setAvisando] = useState(false);
  const ultimaActividad = useRef(Date.now());

  // La inactividad es la del NAVEGADOR, no la de esta pestaña. El médico con el
  // Turno en una pestaña y el Visualizador en otra tenía la de atrás venciendo
  // sola, y `logout()` es global al origen: se llevaba la sesión de la pestaña
  // en la que estaba escribiendo, y con ella el diario del dictado.
  const inactividadMs = () =>
    Date.now() - Math.max(ultimaActividad.current, ultimaActividadConocida());
  const activo = enabled && esEscritorio();

  // En refs para que `marcarActividad` pueda consultarlos sin recrearse en cada
  // render: se registra como listener de mousemove y recrearlo obligaría a
  // desenganchar y reenganchar seis eventos todo el tiempo.
  const onLogoutRef = useRef(onLogout);
  const isBusyRef = useRef(isBusy);
  useEffect(() => {
    onLogoutRef.current = onLogout;
    isBusyRef.current = isBusy;
  }, [onLogout, isBusy]);

  const marcarActividad = useCallback(() => {
    // Una actividad que llega DESPUÉS del vencimiento no lo cancela: cierra.
    //
    // El chequeo no es defensivo, tapa un agujero medido: el navegador
    // throttlea este `setInterval` a una vez por minuto en una pestaña de
    // fondo, y lo congela del todo si la deja mucho rato. Al volver, el primer
    // `mousemove` le gana al tick, y sin esto reseteaba el reloj y resucitaba
    // una sesión que ya estaba muerta. El puesto del hospital con la pestaña
    // atrás es exactamente ese caso.
    const enTrabajo = typeof isBusyRef.current === "function" && isBusyRef.current();
    if (!enTrabajo && inactividadMs() >= IDLE_MS) {
      onLogoutRef.current("al_volver");
      return;
    }
    ultimaActividad.current = Date.now();
    // También en el navegador: si esta pestaña se cierra, el reloj no vuelve a
    // cero. La escritura viene throttleada, no cuesta por evento.
    persistirActividad();
    setAvisando(false);
  }, []);

  useEffect(() => {
    if (!activo) return undefined;
    // La marca inicial no es redundante: sin ella, el que abre la app y cierra
    // la pestaña sin mover el mouse no deja rastro, y al reabrir mañana el
    // chequeo de arranque no tiene contra qué comparar y lo deja pasar.
    persistirActividad({ forzar: true });
    EVENTOS.forEach((ev) => window.addEventListener(ev, marcarActividad, { passive: true }));
    return () => EVENTOS.forEach((ev) => window.removeEventListener(ev, marcarActividad));
  }, [activo, marcarActividad]);

  useEffect(() => {
    if (!activo) return undefined;
    const id = setInterval(() => {
      // Un job en curso cuenta como actividad, aunque nadie toque nada.
      if (typeof isBusy === "function" && isBusy()) {
        ultimaActividad.current = Date.now();
        persistirActividad();
        setAvisando(false);
        return;
      }
      const inactivo = inactividadMs();
      if (inactivo >= IDLE_MS) {
        onLogout("timer");
      } else if (inactivo >= IDLE_MS - AVISO_MS) {
        setAvisando(true);
      }
    }, 15000);
    return () => clearInterval(id);
  }, [activo, isBusy, onLogout]);

  return { avisando, seguirConectado: marcarActividad, activo };
}
